import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Box, Button, Typography, IconButton, useTheme } from "@mui/material";
import uploadImageIcon from "../../../assets/icons/uploadImage.svg";
import { StyledBox, StyledUpoadBox } from "../../../styled";
import UploadedImage from "./UploadedImage";
import { useDeviceSize } from "../../../hooks";
import {
  MOBILE_SIZE_SCREEN,
  SMALL_MOBILE_SIZE_SCREEN,
  UPLOAD_MODAL,
  UPLOAD_VIDEO_MODAL,
} from "../../../constants";
import { openModal } from "../../../store/slice/UISlice";
import VisionStudioSvg from "../../VisionStudioSvg";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchImagesNew } from "../../../store/slice/ProjectImagesUploadedSlice";
import uploadVideoIcon from "../../../assets/icons/video-upload-icon.svg";

interface ImageUploadProps {
  onClose: () => void; // Define the type for onClose
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onClose }) => {
  interface Params extends Record<string, string | undefined> {
    id: string; // 'id' is expected to be a string
  }
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputVideoRef = useRef<HTMLInputElement>(null);

  const deviceSize = useDeviceSize();
  const dispatch: AppDispatch = useDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [imageSrcs, setImageSrcs] = useState<string[]>([]);
  const theme = useTheme();
  const { id } = useParams<Params>();
  const [progress, setProgress] = useState(0);

  const count = 40;

  const handleIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger the hidden input's click
    }
  };

  const handleIconVideoClick = () => {
    if (fileInputVideoRef.current) {
      fileInputVideoRef.current.click(); // Trigger the hidden input's click
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLMediaElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const clearFileInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    try {
      let selectedFiles: File[] = [];
      let ignoredFilesCount = 0;
      let invalidFilesCount = 0;

      const validExtensions = ["png", "jpg", "jpeg"];

      if (event.type === "drop") {
        const droppedFiles = Array.from(
          (event as any).dataTransfer?.files ?? []
        );
        selectedFiles = (droppedFiles as File[]).filter((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (!fileExtension || !validExtensions.includes(fileExtension)) {
            invalidFilesCount++;
            return false;
          }

          if (file.size > 5 * 1024 * 1024) {
            ignoredFilesCount++;
            return false;
          }
          return true;
        });
      } else {
        const inputFiles = Array.from(
          (event.currentTarget as HTMLInputElement).files ?? []
        );
        selectedFiles = (inputFiles as File[]).filter((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (!fileExtension || !validExtensions.includes(fileExtension)) {
            invalidFilesCount++;
            return false;
          }
          if (file.size > 5 * 1024 * 1024) {
            ignoredFilesCount++;
            return false;
          }
          return true;
        });
      }

      if (invalidFilesCount) {
        clearFileInput();
        toast.error(
          `${invalidFilesCount} file${invalidFilesCount === 1 ? "" : "s"
          } have invalid format. Only png, jpg, and jpeg are allowed.`
        );
      }

      if (ignoredFilesCount) {
        clearFileInput();
        toast.error(
          `${ignoredFilesCount} image${ignoredFilesCount === 1 ? "" : "s"
          } exceeded the 5 MB`
        );
      }

      if (selectedFiles.length > 0) {
        const newImageSrcs: string[] = [];
        const newFiles: File[] = [];
        const formData = new FormData();

        // Append other form data
        if (id) {
          formData.append("ProjectId", id);
        } else {
          toast.error("ProjectId is undefined");
          return; // Exit if ProjectId is not defined
        }

        formData.append("Id", "988");

        for (const file of selectedFiles) {
          newFiles.push(file);
          const reader = new FileReader();

          reader.onloadend = async () => {
            newImageSrcs.push(reader.result as string);
            formData.append("Images", file);

            if (newImageSrcs.length === selectedFiles.length) {
              const totalSize = newFiles.reduce(
                (acc, file) => acc + file.size,
                0
              );
              if (totalSize > 300 * 1024 * 1024) {
                clearFileInput();
                toast.error("Total file size exceeds the 300 MB limit!");
                return; // Stop execution if size limit is exceeded
              }

              setImageSrcs(newImageSrcs);
              setFiles(newFiles);

              // Create a new XMLHttpRequest for uploading images
              const xhr = new XMLHttpRequest();
              xhr.open(
                "POST",
                `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/Upload`
              );
              const token = localStorage.getItem("token");
              xhr.setRequestHeader("Authorization", `${token}`);

              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  const percentComplete = (event.loaded / event.total) * 100;
                  const onlyInteger = Math.floor(percentComplete);

                  if (onlyInteger < 100) {
                    setProgress(onlyInteger);
                  }
                }
              };

              // Handle success
              xhr.onload = () => {
                if (xhr.status === 200) {
                  toast.success(
                    `Image${JSON.parse(xhr.response).length === 1 ? "" : "s"
                    } uploaded successfully!`
                  );
                  setProgress(100);
                  dispatch(
                    fetchImagesNew({
                      projectId: id,
                      page: 1,
                      count: count,
                      annotation: "all",
                    })
                  )
                    .unwrap()
                    .then((response) => {
                      if (response.imageUrlModel.length === 0) {
                        dispatch(openModal({ name: UPLOAD_MODAL }));
                      }
                    });
                  const parentElement = document.querySelector(".parent-class");
                  if (parentElement) {
                    parentElement.scrollTo({ top: 0, behavior: "smooth" });
                  }

                  onClose();
                } else {
                  let errorMessage: string;

                  if (typeof xhr.response === "string") {
                    errorMessage = xhr.response;
                  } else if (xhr.response && xhr.response.detail) {
                    errorMessage = xhr.response.detail;
                  } else {
                    errorMessage = "Failed to upload images.";
                  }

                  console.error("Error Response:", xhr.response);
                  toast.error(errorMessage);
                  onClose();
                }
              };

              // Handle error
              xhr.onerror = () => {
                console.error("Error uploading images:", xhr.response);
                toast.error(xhr.response);
              };

              xhr.send(formData);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (error) {
      console.error(error, "error");
    }
  };

  const handleVideoFileChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLMediaElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();

    const file = (event as any).dataTransfer
      ? (event as any).dataTransfer.files[0]
      : (event as any).target.files[0];

    if (file) {
      if (file.size > 150 * 1024 * 1024) {
        toast.error("Please upload videos below 150Mb");
        fileInputVideoRef!.current!.value = "";
        return;
      }
      const parts = file.name.split(".");
      const fileExtension = parts[parts.length - 1];

      if (fileExtension.toLowerCase() === "mp4") {
        dispatch(openModal({ name: UPLOAD_VIDEO_MODAL, videoFile: file }));
      } else {
        //toast.error("Please upload valid file");
        toast.error("Please upload MP4 file.");
        fileInputVideoRef!.current!.value = "";
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography
          sx={{
            fontSize: theme.typography.h6.fontSize,
            fontWeight: theme.typography.h5.fontWeight,
            width: "100%",
          }}
        >
          Upload Images / Video
        </Typography>
        {/* <Button
          variant="contained"
          sx={{
            backgroundColor: "#AABCDE",
            boxShadow: "none",
            fontSize: "12px",
            textTransform: "none"
          }}
        onClick={() => handleDropdownSelect("YOLO")}
        >
          Dataset for YOLO
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#AABCDE",
            boxShadow: "none",
            fontSize: "12px",
            textTransform: "none"
          }}
          onClick={() => handleDropdownSelect("COCO")}
        >
          Dataset for COCO
        </Button> */}
      </Box>      <StyledBox
        sx={{
          backgroundColor: "#FFFFFF",
          marginTop: "20px",
        }}
        width={
          deviceSize === SMALL_MOBILE_SIZE_SCREEN ||
            deviceSize === MOBILE_SIZE_SCREEN
            ? "90%"
            : "85%"
        }
      >
        {imageSrcs.length === 0 && (
          <>
            <StyledUpoadBox
              id="uploadImageUpload"
              // onClick={(e: any) => {
              //   e.stopPropagation();
              //   handleIconClick();
              // }}
              sx={{
                display: "flex",
                // gap: "20px",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-evenly",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  flex: "0 0 49%",
                  padding: "3rem 0 3rem 0",
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Stop bubbling
                  handleIconClick();
                }}
                onDragOver={(e: any) => {
                  e.preventDefault(); // Prevent default to allow drop
                  e.stopPropagation(); // Stop bubbling
                }}
                onDrop={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation(); // Stop bubbling
                  if (e.dataTransfer?.files?.length) {
                    handleFileChange(
                      e as unknown as ChangeEvent<HTMLInputElement>
                    );
                  }
                }}
              >
                <Box>
                  <IconButton>
                    <VisionStudioSvg icon={uploadImageIcon} />
                    <input
                      hidden
                      accept="image/*"
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) =>
                        handleFileChange(
                          e as unknown as ChangeEvent<HTMLInputElement>
                        )
                      }
                    />
                  </IconButton>
                </Box>
                <Box display="flex" width="100%" justifyContent="center">
                  <Typography
                    variant="h6"
                    marginTop="0px"
                    sx={{
                      fontSize: "12px",
                      color: "#132A00",
                    }}
                  >
                    Drop your image here, or
                    <Button
                      component="label"
                      sx={{
                        fontSize: "12px",
                        textTransform: "initial",
                      }}
                      onClick={(e) => e.stopPropagation()} // Prevent bubbling
                    >
                      browse
                      <input
                        hidden
                        accept="image/*"
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) =>
                          handleFileChange(
                            e as unknown as ChangeEvent<HTMLInputElement>
                          )
                        }
                      />
                    </Button>
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: "8px" }}
                  >
                    Supports: PNG, JPG, JPEG
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: "0 0 2px" }}>
                <div
                  style={{ border: "1px solid #A1A1A1", height: "20vh" }}
                ></div>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  flex: "0 0 49%",
                  padding: "3rem 0 3rem 0",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleIconVideoClick();
                }}
                onDragOver={(e: any) => {
                  e.preventDefault(); // Prevent default to allow drop
                  e.stopPropagation(); // Stop bubbling
                }}
                onDrop={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation(); // Stop bubbling
                  if (e.dataTransfer?.files?.length) {
                    handleVideoFileChange(
                      e as unknown as ChangeEvent<HTMLInputElement>
                    );
                  }
                }}
              >
                <Box>
                  <IconButton>
                    <VisionStudioSvg
                      icon={uploadVideoIcon}
                      style={{ padding: "20px" }}
                    />
                    <input
                      hidden
                      accept="video/mp4"
                      ref={fileInputVideoRef}
                      type="file"
                      onChange={(e) =>
                        handleVideoFileChange(
                          e as unknown as ChangeEvent<HTMLInputElement>
                        )
                      }
                    />
                  </IconButton>
                </Box>
                <Box display="flex" width="100%" justifyContent="center">
                  <Typography
                    variant="h6"
                    marginTop="0px"
                    sx={{
                      fontSize: "12px",
                      color: "#132A00",
                    }}
                  >
                    Drop your video here, or
                    <Button
                      component="label"
                      sx={{
                        fontSize: "12px",
                        textTransform: "initial",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      browse
                      <input
                        hidden
                        accept="video/mp4"
                        ref={fileInputVideoRef}
                        type="file"
                        onChange={(e) =>
                          handleVideoFileChange(
                            e as unknown as ChangeEvent<HTMLInputElement>
                          )
                        }
                      />
                    </Button>
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: "8px" }}
                  >
                    Supports: MP4
                  </Typography>
                </Box>
              </Box>
            </StyledUpoadBox>

            {/* <Box marginTop="20px" width="100%">
              <Typography
                sx={{
                  marginBottom: "5px",
                  fontWeight: theme.typography.h5.fontWeight,
                }}
              >
                Import from Url
              </Typography>
              <TextField
                sx={{ padding: "10px 0", "& fieldset": { border: "none" } }}
                fullWidth
                placeholder="Add file URL"
                InputProps={{
                  style: {
                    textAlign: "right",
                    background: "#F1F4F9",
                    // padding: "10px",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="caption"
                        fontSize="12px"
                        fontWeight="500"
                      >
                        Upload
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box> */}
            {/* <Box
              display="flex"
              justifyContent="space-between"
              marginTop="20px"
              width="100%"
              alignItems="center"
            >
              <Box display="flex">
                <Box>
                  <img
                    src={helpCenterIcon}
                    alt="Help Center"
                    style={{ paddingTop: "0.3rem" }}
                  />
                </Box>
                <Typography
                  component="p"
                  ml="0.5rem"
                  mt="0.1rem"
                  color="#1a1a1a70"
                  fontSize={theme.typography.caption.fontSize}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  Help Center
                </Typography>
              </Box>
              <Box display="flex">
                <Box>
                  <StylexButton
                    variant="contained"
                    color="inherit"
                    sx={{
                      paddingTop: "0.4rem",
                      backgroundColor: "#FFFFFF",
                      boxShadow: "none",
                      border: "1px solid  #1A1A1A2E",
                    }}
                    onClick={onClose}
                  >
                    Cancel
                  </StylexButton>
                </Box>
                <Box ml="0.5rem">
                  <StylexButton
                    variant="contained"
                    color="inherit"
                    sx={{
                      backgroundColor: "#AABCDE",
                      boxShadow: "none",
                    }}
                  >
                    Done
                  </StylexButton>
                </Box>
              </Box>
            </Box> */}
          </>
        )}

        {imageSrcs.length > 0 && (
          <Box
            sx={{
              border: "1px solid #E4E9F0",
              padding: "12px",
              borderRadius: "12px",
              width: "96%",
            }}
          >
            <UploadedImage
              imageUrl={imageSrcs}
              files={files}
              progress={progress}
            />
          </Box>
        )}
      </StyledBox>
    </Box>
  );
};

export default ImageUpload;
